import { Controller } from "@hotwired/stimulus";
import { createRoot } from "react-dom/client";
import { Application } from "./application";
import type { LocationSuggestion } from "./lib/types";

// Stimulus controller name (auto-registered): `location-analysis`
// Mount point: <div id="location_analysis_shell" data-controller="location-analysis"></div>
//
// Server-driven props (data-* attrs on the mount div):
//   data-preselected-location  → JSON of the deep-link Location
//   data-preselect-error       → soft error from slug resolution
//   data-agent-not-configured  → "true" when no qr_code agent is configured
//
// Quota state is NOT passed in; the form always renders, and the React
// app flips to the QuotaExhausted upsell only on receiving a 403 from
// the analyse endpoint.
export default class extends Controller {
  private root: ReturnType<typeof createRoot> | null = null;

  connect() {
    document.addEventListener("turbo:before-cache", this.beforeCache);

    if (!this.root) {
      this.root = createRoot(this.element as HTMLElement);
      const el = this.element as HTMLElement;

      // Optional: set on the /emplacement/:slug route only. Carries a
      // pre-resolved Location so the React app skips the autocomplete
      // and auto-fires the analysis. Malformed JSON falls back to
      // undefined (treated as "not preselected").
      let preselectedLocation: LocationSuggestion | undefined;
      const raw = el.dataset.preselectedLocation;
      if (raw) {
        try {
          preselectedLocation = JSON.parse(raw) as LocationSuggestion;
        } catch {
          preselectedLocation = undefined;
        }
      }

      const preselectError = el.dataset.preselectError || undefined;

      // Server-side pre-flight: no published qr_code agent exists for
      // profile_class="Location". The page renders a "not configured"
      // banner and suppresses any auto-fire so the visitor doesn't see
      // a 422 loop.
      const agentNotConfigured = el.dataset.agentNotConfigured === "true";

      this.root.render(
        <Application
          preselectedLocation={preselectedLocation}
          preselectError={preselectError}
          agentNotConfigured={agentNotConfigured}
        />,
      );
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache);

    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  beforeCache = () => {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  };
}
